import type {
  ProgressiveDrawRatingParams,
  RatingCategory,
  VariantProgressiveDrawRatingParams,
} from "~/domain/rating/ratingAlgorithm";
import { CurrentRatingAlgorithm } from "~/domain/rating/currentRatingAlgorithm";
import { SessionTaskAttemptResultEnum } from "~/lib/objects";

export type { ProgressiveDrawRatingParams, VariantProgressiveDrawRatingParams };

const ratingAlgorithm = new CurrentRatingAlgorithm();

function toRatingCategory(result: SessionTaskAttemptResultEnum): RatingCategory {
  if (result === SessionTaskAttemptResultEnum.LOSS) {
    return "loss";
  }

  if (result === SessionTaskAttemptResultEnum.DRAW) {
    return "draw";
  }

  return "win";
}

export function calculateProgressiveDrawCoefficient(
  params: ProgressiveDrawRatingParams,
): number {
  return ratingAlgorithm.calculateProgressiveDrawCoefficient(params);
}

/**
 * Pure helper to calculate rating delta using lifeRating as base.
 * - lifeRating: base used for Duel calculations
 * - taskRating: opponent rating (problem rating)
 * - result: WIN/DRAW/LOSS
 * - retryCountUsed: number of retries used by the player (scales positives)
 * - kFactor: optional Duel k factor (default 40)
 *
 * Returns a signed delta: positive for wins/draws, negative for losses.
 */
export function calculateRatingDeltaFromLife(
  lifeRating: number,
  taskRating: number,
  result: SessionTaskAttemptResultEnum,
  retryCountUsed = 0,
  kFactor = 40,
  progressiveDrawRatingParams?: ProgressiveDrawRatingParams,
): number {
  const category = toRatingCategory(result);
  const drawCoefficient =
    category === "draw" && progressiveDrawRatingParams
      ? ratingAlgorithm.calculateProgressiveDrawCoefficient(
          progressiveDrawRatingParams,
        )
      : undefined;

  return ratingAlgorithm.calculateDelta({
    lifeRating,
    taskRating,
    category,
    retryCountUsed,
    kFactor,
    drawCoefficient,
  });
}

export function calculateProgressiveDrawDeltaFromCoefficient(
  lifeRating: number,
  taskRating: number,
  retryCountUsed: number,
  kFactor: number,
  coefficient: number,
): number {
  return ratingAlgorithm.calculateProgressiveDrawDeltaFromCoefficient({
    lifeRating,
    taskRating,
    retryCountUsed,
    kFactor,
    coefficient,
  });
}

export function calculateVariantProgressiveDrawDelta(
  lifeRating: number,
  taskRating: number,
  retryCountUsed: number,
  kFactor: number,
  params: VariantProgressiveDrawRatingParams,
): number {
  return ratingAlgorithm.calculateVariantProgressiveDrawDelta({
    lifeRating,
    taskRating,
    retryCountUsed,
    kFactor,
    params,
  });
}
